import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  canShowIntegrationDashboardBridge,
  getIntegrationMode,
} from '../../app/helpers/semanticSearchConfigHelper';
import { startRegisterUserInterest } from '../../app/actions/userInterestActions';
import { registerCompareInterest } from '../../app/helpers/intentTrackingHelper';
import ComparisonPanel from './ComparisonPanel';
import IntegrationResultsPanel from './IntegrationResultsPanel';
import VerticalToolbar from './VerticalToolbar';
import { getSelectedOriginItems, MODULE_INTEGRATION_META } from './moduleIntegrationMeta';

/**
 * Toolbar + ComparisonPanel + IntegrationResultsPanel para L3 containers.
 */
const IntegrationSearchChrome = ({
  hasResults,
  moduleSelected,
  searchText = '',
  showComparison = true,
}) => {
  const dispatch = useDispatch();
  const configuration = useSelector((state) => state.configuration);
  const moduleKey = moduleSelected?.key;

  const moduleState = useSelector((state) => {
    if (!moduleKey || !MODULE_INTEGRATION_META[moduleKey]) return null;
    const map = {
      technology: state.technology,
      alternative: state.alternative,
      experience: state.experience,
      innovation: state.innovation,
      assistance: state.assistance,
      financial: state.financial,
      vision: state.vision,
      regulation: state.regulation,
    };
    return map[moduleKey];
  });

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [comparisonMode, setComparisonMode] = useState('field');
  const [integrationOpen, setIntegrationOpen] = useState(false);
  const [targetModule, setTargetModule] = useState(null);
  const [originItemsSnapshot, setOriginItemsSnapshot] = useState([]);

  const originItems = useMemo(
    () => getSelectedOriginItems(moduleState, moduleKey),
    [moduleState, moduleKey]
  );

  const onCompareClick = (mode) => {
    setComparisonMode(mode);
    setIsPanelOpen(true);
    registerCompareInterest(
      dispatch,
      moduleKey,
      originItems.map((o) => o.id)
    );
  };

  const onIntegrationClick = (targetKey) => {
    if (!canShowIntegrationDashboardBridge(configuration, moduleKey)) return;
    if (!originItems.length) return;
    setOriginItemsSnapshot(originItems);
    setTargetModule(targetKey);
    setIntegrationOpen(true);
    dispatch(
      startRegisterUserInterest({
        event_type: 'bridge_click',
        sourceModule: moduleKey,
        targetModule: targetKey,
        originIds: originItems.map((o) => o.id),
        bridge_kind: 'dashboard',
        searchText:
          searchText ||
          (typeof localStorage !== 'undefined'
            ? localStorage.getItem('searchTextLocal') || ''
            : ''),
      })
    );
  };

  const mode = getIntegrationMode(configuration);

  return (
    <>
      <VerticalToolbar
        hasRecords={hasResults}
        onCompareClick={showComparison ? onCompareClick : undefined}
        onIntegrationClick={onIntegrationClick}
        moduleSelected={moduleSelected}
        variant="search"
      />

      {showComparison && (
        <ComparisonPanel
          isOpen={isPanelOpen}
          onClose={() => setIsPanelOpen(false)}
          comparisonMode={comparisonMode}
          moduleSelected={moduleSelected}
        />
      )}

      <IntegrationResultsPanel
        isOpen={integrationOpen}
        onClose={() => setIntegrationOpen(false)}
        sourceModuleKey={moduleKey}
        targetModule={targetModule}
        originItems={originItemsSnapshot}
        searchText={searchText || (typeof localStorage !== 'undefined' ? localStorage.getItem('searchTextLocal') || '' : '')}
        mode={mode}
        bridgeKind="dashboard"
      />
    </>
  );
};

export default IntegrationSearchChrome;
